import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { lastValueFrom, map, Observable, startWith } from 'rxjs';
import {
  GetProvidersByServiceEnvelope,
  isProvidersEmptySuccess,
  isProvidersFound,
  ProveedorTarifasItem,
  toProvidersByServiceClientError,
} from 'src/app/cuadro-picnic/lib/get-providers-by-service';
import { SalesService } from 'src/app/services/api/sales.service';
import { SourcingService } from 'src/app/services/api/sourcing.service';
import { CognitoService } from 'src/app/services/cognito.service';
import Swal from 'sweetalert2';

/**
 * Information structure representing a provider option in the selection list.
 */
export interface ProviderSelectionInfo {
  id: string;
  nombre: string;
  tipoProveedor?: string;
  correoReserva?: string;
  oldServiceId?: string;
  servicioId?: string;
}

/**
 * Result structure returned after processing provider updates for a specific service.
 */
export interface ServiceProviderUpdateResult {
  serviceId: string;
  serviceName: string;
  success: boolean;
  message: string;
}

/**
 * Result payload returned upon closing DialogProviderMultipleComponent.
 */
export interface DialogProviderMultipleResult {
  success: boolean;
  partial?: boolean;
  nombreProveedor: string;
  providerName: string;
  idProveedor: string;
  providerId: string;
  successfulServiceIds: string[];
  failedServiceIds: string[];
  results: ServiceProviderUpdateResult[];
  updatedCount: number;
}

/**
 * Dialog data contract passed into DialogProviderMultipleComponent.
 */
export interface DialogProviderMultipleData {
  filtered: Array<{
    id: string;
    nombreServicio: string;
    tipoServicio?: string;
    categoriaServicio?: string;
    locacion?: string;
    serviceSnapshot?: {
      locacion?: string;
      nombreServicio?: string;
      tipoServicio?: string;
      categoriaServicio?: string;
      [key: string]: unknown;
    };
    nombreProveedor?: string;
    [key: string]: unknown;
  }>;
  modalTitle?: string;
}

@Component({
  selector: 'app-dialog-provider-multiple',
  templateUrl: './dialog-provider-multiple.component.html',
  styleUrls: ['./dialog-provider-multiple.component.scss'],
})
export class DialogProviderMultipleComponent implements OnInit {
  /** Filtered list of common providers displayed to the user */
  public providerList: ProviderSelectionInfo[] = [];

  /** Full list of provider records indexed with service associations */
  public providerAllList: Array<
    ProveedorTarifasItem & { oldServiceId: string }
  > = [];

  /** Form control for provider autocomplete selection */
  public providerSearchControl = new FormControl<
    string | ProviderSelectionInfo
  >('', { nonNullable: true });

  /** Loading state while querying provider catalog */
  public isLoadingCatalog = false;

  /** Loading and overlay state while executing batch updates */
  public isProcessingBatch = false;

  /** Status message returned by backend or client validator */
  public providersCatalogMessage: string | null = null;

  /** Flag indicating whether the available providers list is empty */
  public isProvidersListEmpty = false;

  /** Name of the service currently undergoing update */
  public activeProcessingServiceName = '';

  /** Counter of completed service updates */
  public completedServicesCount = 0;

  /** Total count of services scheduled for update */
  public totalServicesCount = 0;

  /** Logged-in user email identifier */
  public authenticatedUserEmail: string | null = null;

  /** Reactive observable for filtering autocomplete options */
  public filteredProviderOptions$!: Observable<ProviderSelectionInfo[]>;

  constructor(
    public readonly dialogReference: MatDialogRef<DialogProviderMultipleComponent>,
    @Inject(MAT_DIALOG_DATA)
    public readonly dialogData: DialogProviderMultipleData,
    private readonly sourcingService: SourcingService,
    private readonly salesService: SalesService,
    private readonly cognitoService: CognitoService,
  ) {}

  /**
   * Initializes the component, resolves user identity, and triggers provider discovery.
   *
   * Description:
   *   Authenticates active user session through Cognito and loads Sourcing catalog providers.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   Promise<void> - Resolves when initialization completes.
   *
   * Exceptions & Errors:
   *   Catches authentication errors and displays error prompt.
   */
  public async ngOnInit(): Promise<void> {
    try {
      this.authenticatedUserEmail = await this.resolveAuthenticatedUserEmail();
    } catch (authenticationError) {
      console.error('Error de autenticación de usuario:', authenticationError);
      this.displayOperationError(
        authenticationError instanceof Error
          ? authenticationError.message
          : 'Usuario no identificado. No se puede continuar sin una sesión activa.',
      );
      return;
    }

    this.filteredProviderOptions$ =
      this.providerSearchControl.valueChanges.pipe(
        startWith(''),
        map((searchQuery) => {
          const queryText =
            typeof searchQuery === 'string'
              ? searchQuery
              : searchQuery?.nombre || '';
          return queryText
            ? this.filterProviderOptions(queryText)
            : this.providerList.slice();
        }),
      );

    void this.loadAvailableProviders();
  }

  /**
   * Resolves the authenticated user email strictly through the Cognito authentication domain service.
   *
   * Description:
   *   Calls CognitoService.getAuthenticatedUserEmail.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   Promise<string> - The authenticated user's email string.
   *
   * Exceptions & Errors:
   *   Throws Error when no active session exists.
   */
  private resolveAuthenticatedUserEmail(): Promise<string> {
    return this.cognitoService.getAuthenticatedUserEmail();
  }

  /**
   * Filters the active provider list by substring match on the provider name.
   *
   * Description:
   *   Performs case-insensitive substring matching against candidate providers.
   *
   * Parameters:
   *   queryText: string - Search text to filter by.
   *
   * Return Value:
   *   ProviderSelectionInfo[] - Matching provider options.
   *
   * Exceptions & Errors:
   *   None.
   */
  private filterProviderOptions(queryText: string): ProviderSelectionInfo[] {
    const normalizedQuery = queryText.toLowerCase().trim();
    return this.providerList.filter((providerOption) =>
      providerOption.nombre.toLowerCase().includes(normalizedQuery),
    );
  }

  /**
   * Closes the active dialog when clicking cancel or outside, preventing closure during batch execution.
   *
   * Description:
   *   Dismisses the dialog if not actively executing batch updates.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public handleCancelClick(): void {
    if (this.isProcessingBatch) {
      return;
    }
    this.dialogReference.close();
  }

  /**
   * Formats the displayed label in the Material Autocomplete input.
   *
   * Description:
   *   Extracts provider name from selected entity.
   *
   * Parameters:
   *   providerOption: ProviderSelectionInfo | null | undefined - Selected provider entity.
   *
   * Return Value:
   *   string - Formatted provider name.
   *
   * Exceptions & Errors:
   *   None.
   */
  public getOptionDisplayLabel(
    providerOption: ProviderSelectionInfo | null | undefined,
  ): string {
    return providerOption?.nombre || '';
  }

  /**
   * Queries the Sourcing catalog in parallel for all target services and computes common providers.
   *
   * Description:
   *   Gathers provider lists for all selected services and finds the intersection.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   Promise<void> - Resolves when catalog discovery completes.
   *
   * Exceptions & Errors:
   *   Catches API failures per service and records status messages.
   */
  public async loadAvailableProviders(): Promise<void> {
    this.isLoadingCatalog = true;
    this.providerList = [];
    this.providerAllList = [];
    this.providersCatalogMessage = null;
    this.isProvidersListEmpty = false;

    const providerSetsPerService: Set<string>[] = [];
    const providersGroupedByService: ProveedorTarifasItem[][] = [];
    const statusMessagesCollection: string[] = [];
    const targetServicesList = Array.isArray(this.dialogData?.filtered)
      ? this.dialogData.filtered
      : [];

    try {
      await Promise.all(
        targetServicesList.map(async (serviceEntity) => {
          const serviceNameQuery = serviceEntity?.nombreServicio ?? '';
          const serviceCategoryQuery =
            serviceEntity?.categoriaServicio &&
            serviceEntity.categoriaServicio !== 'null'
              ? serviceEntity.categoriaServicio
              : '';
          const serviceLocationQuery =
            serviceEntity?.locacion ||
            serviceEntity?.serviceSnapshot?.locacion ||
            '';

          let fetchedProvidersList: ProveedorTarifasItem[] = [];
          try {
            const envelopeResult: GetProvidersByServiceEnvelope =
              await lastValueFrom(
                this.sourcingService.getProvidersByService(
                  serviceNameQuery,
                  serviceCategoryQuery,
                  serviceLocationQuery,
                ),
              );

            if (envelopeResult?.message) {
              statusMessagesCollection.push(envelopeResult.message);
            }

            if (isProvidersFound(envelopeResult.code)) {
              fetchedProvidersList = envelopeResult.data ?? [];
            } else if (isProvidersEmptySuccess(envelopeResult.code)) {
              fetchedProvidersList = [];
            } else {
              fetchedProvidersList = envelopeResult.data ?? [];
            }
          } catch (serviceLookupError) {
            const parsedClientError =
              toProvidersByServiceClientError(serviceLookupError);
            console.error(
              'Error getProvidersByService for service:',
              serviceEntity?.id,
              serviceNameQuery,
              parsedClientError,
            );
            statusMessagesCollection.push(parsedClientError.message);
            fetchedProvidersList = [];
          }

          providersGroupedByService.push(fetchedProvidersList);

          for (const rawProviderItem of fetchedProvidersList) {
            this.providerAllList.push({
              ...rawProviderItem,
              oldServiceId: serviceEntity.id,
            });
          }

          const providerIdsSet = new Set<string>(
            fetchedProvidersList
              .map((providerItem) => providerItem.id)
              .filter((id): id is string => Boolean(id)),
          );
          providerSetsPerService.push(providerIdsSet);
        }),
      );

      let commonProviderIdsSet: Set<string> =
        providerSetsPerService.length > 0
          ? new Set(providerSetsPerService[0])
          : new Set();

      for (const singleServiceSet of providerSetsPerService) {
        commonProviderIdsSet = new Set(
          [...commonProviderIdsSet].filter((providerId) =>
            singleServiceSet.has(providerId),
          ),
        );
      }

      const uniqueProvidersMap = new Map<string, ProviderSelectionInfo>();
      for (const providersList of providersGroupedByService) {
        for (const providerItem of providersList) {
          if (
            providerItem?.id &&
            providerItem?.nombre &&
            commonProviderIdsSet.has(providerItem.id) &&
            !uniqueProvidersMap.has(providerItem.id)
          ) {
            uniqueProvidersMap.set(providerItem.id, {
              id: providerItem.id,
              nombre: providerItem.nombre,
              tipoProveedor: providerItem.tipoProveedor || undefined,
              correoReserva: providerItem.correoReserva || undefined,
              servicioId: providerItem.servicioId || undefined,
            });
          }
        }
      }

      const deduplicatedNamesSet = new Set<string>();
      this.providerList = [...uniqueProvidersMap.values()].filter(
        (providerCandidate) => {
          if (
            !providerCandidate.nombre ||
            deduplicatedNamesSet.has(providerCandidate.nombre)
          ) {
            return false;
          }
          deduplicatedNamesSet.add(providerCandidate.nombre);
          return true;
        },
      );

      this.providersCatalogMessage =
        statusMessagesCollection.find(Boolean) ??
        (this.providerList.length === 0
          ? (statusMessagesCollection[0] ?? null)
          : null);
      this.isProvidersListEmpty = this.providerList.length === 0;

      if (this.providersCatalogMessage && this.providerList.length > 0) {
        void Swal.fire({
          icon: 'info',
          title: 'Proveedores',
          text: this.providersCatalogMessage,
          timer: 2200,
          showConfirmButton: false,
          toast: true,
          position: 'top-end',
        });
      }
    } finally {
      this.isLoadingCatalog = false;
    }
  }

  /**
   * Executes the sequential update of the selected provider across all target services.
   *
   * Description:
   *   Iterates through target services, executes changeProvider mutation individually,
   *   tracks success/failure per service ID, and emits structured results.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   Promise<void> - Resolves when batch processing completes.
   *
   * Exceptions & Errors:
   *   Handles validation errors and network exceptions during batch processing.
   */
  public async handleSaveChangeProvider(): Promise<void> {
    const selectedProviderValue = this.providerSearchControl.value;
    let selectedProviderId =
      typeof selectedProviderValue === 'object' &&
      selectedProviderValue !== null
        ? selectedProviderValue.id
        : null;
    let selectedProviderName =
      typeof selectedProviderValue === 'object' &&
      selectedProviderValue !== null
        ? selectedProviderValue.nombre
        : typeof selectedProviderValue === 'string'
          ? selectedProviderValue.trim()
          : '';

    if (!selectedProviderId && selectedProviderName) {
      const matchByName = this.providerList.find(
        (providerCandidate) =>
          providerCandidate.nombre.toLowerCase() ===
          selectedProviderName.toLowerCase(),
      );
      if (matchByName) {
        selectedProviderId = matchByName.id;
        selectedProviderName = matchByName.nombre;
      }
    }

    if (!selectedProviderId || !selectedProviderName) {
      void Swal.fire({
        icon: 'warning',
        title: 'Selección Requerida',
        text: 'Por favor seleccione un proveedor válido de la lista desplegable.',
        confirmButtonColor: '#e95a1a',
      });
      return;
    }

    const servicesToUpdate = Array.isArray(this.dialogData?.filtered)
      ? this.dialogData.filtered
      : [];

    if (servicesToUpdate.length === 0) {
      this.displayOperationError(
        'No hay servicios disponibles para actualizar.',
      );
      return;
    }

    if (!this.authenticatedUserEmail) {
      try {
        this.authenticatedUserEmail =
          await this.resolveAuthenticatedUserEmail();
      } catch (authResolutionError) {
        this.displayOperationError(
          authResolutionError instanceof Error
            ? authResolutionError.message
            : 'Usuario no identificado. No se puede realizar el cambio de proveedor sin una sesión activa.',
        );
        return;
      }
    }

    this.isProcessingBatch = true;
    this.completedServicesCount = 0;
    this.totalServicesCount = servicesToUpdate.length;
    this.activeProcessingServiceName = 'Iniciando proceso...';

    try {
      const operationResultsList: ServiceProviderUpdateResult[] = [];

      for (
        let serviceIndex = 0;
        serviceIndex < servicesToUpdate.length;
        serviceIndex++
      ) {
        const targetService = servicesToUpdate[serviceIndex];
        this.activeProcessingServiceName =
          targetService.nombreServicio || `Servicio ${serviceIndex + 1}`;
        this.completedServicesCount = serviceIndex;

        const singleUpdateResult = await this.executeSingleServiceUpdate(
          targetService,
          selectedProviderName,
        );
        operationResultsList.push(singleUpdateResult);

        this.completedServicesCount = serviceIndex + 1;
      }

      this.activeProcessingServiceName = 'Completado';
      this.finalizeBatchOperation(
        operationResultsList,
        selectedProviderName,
        selectedProviderId,
      );
    } catch (batchExecutionError) {
      console.error(
        'Error inesperado durante la ejecución batch de proveedores:',
        batchExecutionError,
      );
      const detailedErrorMessage =
        batchExecutionError instanceof Error
          ? batchExecutionError.message
          : 'Ocurrió un error inesperado al procesar la actualización.';
      this.displayOperationError(detailedErrorMessage);
    } finally {
      this.isProcessingBatch = false;
    }
  }

  /**
   * Executes changeProvider mutation for a single target service record.
   *
   * Description:
   *   Finds matching provider candidate and invokes salesService.changeProvider GraphQL mutation.
   *
   * Parameters:
   *   targetService: { id: string; nombreServicio?: string; [key: string]: unknown } - Service entity.
   *   targetProviderName: string - Name of the chosen provider.
   *
   * Return Value:
   *   Promise<ServiceProviderUpdateResult> - Individual update outcome with serviceId, success boolean, and message.
   *
   * Exceptions & Errors:
   *   Catches mutation and network errors, returning formatted failure outcome.
   */
  private async executeSingleServiceUpdate(
    targetService: {
      id: string;
      nombreServicio?: string;
      [key: string]: unknown;
    },
    targetProviderName: string,
  ): Promise<ServiceProviderUpdateResult> {
    const serviceNameString = targetService.nombreServicio || targetService.id;

    try {
      const candidateProvidersForService = this.providerAllList.filter(
        (providerRecord) => providerRecord.oldServiceId === targetService.id,
      );

      const matchingProvider = candidateProvidersForService.find(
        (providerItem) => providerItem.nombre === targetProviderName,
      );

      if (!matchingProvider || !matchingProvider.id) {
        return {
          serviceId: targetService.id,
          serviceName: serviceNameString,
          success: false,
          message: `Proveedor "${targetProviderName}" no disponible para el servicio ${serviceNameString}`,
        };
      }

      const idRegistroUuid = targetService.id;
      const idProviderUuid = matchingProvider.id;
      const sourcingCatalogServiceId = matchingProvider.servicioId || '';
      const userIdentifier = this.authenticatedUserEmail!;

      const mutationResponse = await lastValueFrom(
        this.salesService.changeProvider(
          idRegistroUuid,
          idProviderUuid,
          userIdentifier,
          sourcingCatalogServiceId,
        ),
      );

      const mutationResult = mutationResponse?.changeProvider;
      if (!mutationResult) {
        return {
          serviceId: targetService.id,
          serviceName: serviceNameString,
          success: false,
          message: 'Sin respuesta del servidor de ventas',
        };
      }

      return {
        serviceId: targetService.id,
        serviceName: serviceNameString,
        success: Boolean(mutationResult.success),
        message:
          mutationResult.message ||
          (mutationResult.success
            ? 'Actualizado correctamente'
            : 'Error desconocido en la mutación'),
      };
    } catch (mutationError) {
      console.error(
        `Error al ejecutar changeProvider para servicio ${targetService.id}:`,
        mutationError,
      );
      const errorMessage =
        mutationError instanceof Error
          ? mutationError.message
          : 'Error de red o GraphQL';
      return {
        serviceId: targetService.id,
        serviceName: serviceNameString,
        success: false,
        message: `Error en servicio ${serviceNameString}: ${errorMessage}`,
      };
    }
  }

  /**
   * Analyzes the collection of update results, displays appropriate user alerts, and closes the modal.
   *
   * Description:
   *   Categorizes successful vs failed updates, displays SweetAlert notifications, and emits
   *   granular lists of successful and failed service IDs to the parent dialog.
   *
   * Parameters:
   *   resultsList: ServiceProviderUpdateResult[] - Results array from all processed services.
   *   updatedProviderName: string - Name of the newly assigned provider.
   *   updatedProviderId: string - PK ID of the newly assigned provider.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  private finalizeBatchOperation(
    resultsList: ServiceProviderUpdateResult[],
    updatedProviderName: string,
    updatedProviderId: string,
  ): void {
    const successfulUpdates = resultsList.filter((item) => item.success);
    const failedUpdates = resultsList.filter((item) => !item.success);
    const successfulServiceIds = successfulUpdates.map(
      (item) => item.serviceId,
    );
    const failedServiceIds = failedUpdates.map((item) => item.serviceId);

    if (failedUpdates.length === 0) {
      void Swal.fire({
        icon: 'success',
        title: 'Éxito',
        text: `Proveedor "${updatedProviderName}" actualizado correctamente en ${successfulUpdates.length} servicio(s)`,
        timer: 2200,
        showConfirmButton: false,
        position: 'top-end',
        toast: true,
      });

      this.dialogReference.close({
        success: true,
        partial: false,
        nombreProveedor: updatedProviderName,
        providerName: updatedProviderName,
        idProveedor: updatedProviderId,
        providerId: updatedProviderId,
        successfulServiceIds,
        failedServiceIds,
        results: resultsList,
        updatedCount: successfulUpdates.length,
      } as DialogProviderMultipleResult);
    } else if (successfulUpdates.length === 0) {
      const aggregatedErrors = failedUpdates
        .map((failedItem) => `${failedItem.serviceName}: ${failedItem.message}`)
        .join('; ');
      void Swal.fire({
        icon: 'error',
        title: 'Error de Actualización',
        text: aggregatedErrors || 'No se pudo actualizar ningún servicio.',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#d33',
      });

      this.dialogReference.close({
        success: false,
        partial: false,
        nombreProveedor: updatedProviderName,
        providerName: updatedProviderName,
        idProveedor: updatedProviderId,
        providerId: updatedProviderId,
        successfulServiceIds: [],
        failedServiceIds,
        results: resultsList,
        updatedCount: 0,
      } as DialogProviderMultipleResult);
    } else {
      const aggregatedErrors = failedUpdates
        .map((failedItem) => `${failedItem.serviceName}: ${failedItem.message}`)
        .join('; ');
      void Swal.fire({
        icon: 'warning',
        title: 'Actualización Parcial',
        text: `${successfulUpdates.length} exitoso(s), ${failedUpdates.length} fallido(s). Detalle: ${aggregatedErrors}`,
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#e95a1a',
      });

      this.dialogReference.close({
        success: true,
        partial: true,
        nombreProveedor: updatedProviderName,
        providerName: updatedProviderName,
        idProveedor: updatedProviderId,
        providerId: updatedProviderId,
        successfulServiceIds,
        failedServiceIds,
        results: resultsList,
        updatedCount: successfulUpdates.length,
      } as DialogProviderMultipleResult);
    }
  }

  /**
   * Displays an error alert dialog and closes the modal.
   *
   * Description:
   *   Presents modal error alert and dismisses DialogProviderMultipleComponent.
   *
   * Parameters:
   *   errorMessage: string - Error description to display.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  private displayOperationError(errorMessage: string): void {
    void Swal.fire({
      icon: 'error',
      title: 'Error',
      text: errorMessage,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#d33',
    });
    this.dialogReference.close();
  }
}
